import React from 'react';
import { motion } from 'framer-motion';
import { Rocket, Clock, Shield, Mic, CheckCircle2, Zap, Users, Target } from 'lucide-react';

export const SocialProof = () => {
  const capabilities = [
    { value: "8+ hrs", label: "Saved weekly per rep", icon: Clock },
    { value: "98%", label: "Voice recognition accuracy", icon: Mic },
    { value: "<30s", label: "Average CRM update", icon: Zap },
    { value: "100%", label: "GDPR compliant", icon: Shield }
  ];

  const betaFeedback = [
    {
      quote: "Finally, I can update HubSpot without touching my laptop.",
      role: "Enterprise AE",
      context: "Beta tester feedback"
    },
    {
      quote: "This is what HubSpot should have built years ago.",
      role: "SDR Team Lead", 
      context: "Beta tester feedback"
    },
    {
      quote: "My forecast accuracy improved because reps actually update deals now.",
      role: "Sales Director",
      context: "Beta tester feedback"
    }
  ];

  return (
    <section className="w-full py-20 md:py-32 bg-background relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        {/* Launch announcement banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 text-primary font-semibold">
            <Rocket className="w-4 h-4 animate-pulse" />
            <span>Just Launched — Be Among The First</span>
          </div>
        </motion.div>

        {/* Capabilities bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-3xl bg-gradient-to-br from-primary to-primary/90 text-primary-foreground">
            {capabilities.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <item.icon className="w-6 h-6 mx-auto mb-2 opacity-80" />
                <div className="text-3xl md:text-4xl font-bold mb-1">{item.value}</div>
                <div className="text-sm text-primary-foreground/70">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Shaped By <span className="text-gradient-teal">Real Sales Teams</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every feature was built from feedback by sales reps who live in HubSpot daily. Here's what they told us.
          </p>
        </motion.div>

        {/* Beta tester feedback */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {betaFeedback.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="h-full p-8 rounded-3xl bg-card border border-border hover:border-primary/50 shadow-subtle hover:shadow-card transition-all duration-300">
                <div className="text-4xl text-primary/30 mb-4">"</div>
                <p className="text-lg text-foreground mb-6 italic">{item.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">{item.role}</div>
                    <div className="text-sm text-muted-foreground">{item.context}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Why early adopters win */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="rounded-3xl bg-gradient-to-br from-muted/50 to-muted/30 border border-border p-8 md:p-12"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Target className="w-4 h-4" />
                Early Adopter Benefits
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Get in early. Shape the product.
              </h3>
              <ul className="space-y-3">
                {[
                  "Lock in founding member pricing forever",
                  "Direct line to the founder for feature requests",
                  "Priority support and onboarding",
                  "Influence the roadmap based on your workflow"
                ].map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3 text-muted-foreground">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
                <div className="relative bg-gradient-to-br from-primary to-primary-glow p-8 rounded-2xl text-center">
                  <div className="text-5xl font-bold text-primary-foreground mb-2">7</div>
                  <div className="text-primary-foreground/80 text-sm">Day Free Trial</div>
                  <div className="text-primary-foreground/60 text-xs mt-1">No credit card required</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};